import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SettingsService } from '@services/settings.service';
import { NotificationService } from '@services/notification.service';
import { MaterialOption } from '@models/settings';

@Component({
  selector: 'app-duty-settings',
  templateUrl: './duty-settings.component.html',
  styleUrls: ['./duty-settings.component.css'],
})
export class DutySettingsComponent implements OnInit {
  pricingForm: FormGroup;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private notificationService: NotificationService,
    private cdRef: ChangeDetectorRef
  ) {
    this.pricingForm = this.fb.group({
      dutyAmount: ['', [Validators.required, Validators.min(0)]],
      nosepadsInstallAmount: ['', [Validators.required, Validators.min(0)]],
      drillMountAmount: ['', [Validators.required, Validators.min(0)]],
      materials: this.fb.array([]),
    });
  }

  get materials(): FormArray {
    return this.pricingForm.get('materials') as FormArray;
  }

  ngOnInit() {
    this.settingsService.loadSettings().subscribe({
      next: (settings) => {
        this.applyPricingToForm(settings);
      },
      error: () => {
        this.notificationService.show('error', 'FAILED TO LOAD PRICING SETTINGS');
      },
    });
  }

  createMaterialGroup(option?: Partial<MaterialOption>): FormGroup {
    return this.fb.group({
      label: [option?.label ?? '', [Validators.required]],
      price: [this.formatPriceValue(option?.price), [Validators.required, Validators.min(0)]],
    });
  }

  formatMaterialPrice(index: number): void {
    const control = this.materials.at(index)?.get('price');
    if (!control) {
      return;
    }
    control.setValue(this.formatPriceValue(control.value), { emitEvent: false });
  }

  private formatPriceValue(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    const parsed = parseFloat(String(value));
    if (isNaN(parsed)) {
      return '';
    }
    return (Math.round(parsed * 100) / 100).toFixed(2);
  }

  setMaterials(options: MaterialOption[]): void {
    this.materials.clear();
    (options || []).forEach((option) => this.materials.push(this.createMaterialGroup(option)));
    this.cdRef.detectChanges();
  }

  addMaterial(): void {
    this.materials.push(this.createMaterialGroup());
  }

  removeMaterial(index: number): void {
    this.materials.removeAt(index);
  }

  save() {
    if (this.pricingForm.invalid) {
      this.pricingForm.markAllAsTouched();
      return;
    }

    const dutyAmount = parseFloat(this.pricingForm.value.dutyAmount);
    const nosepadsInstallAmount = parseFloat(this.pricingForm.value.nosepadsInstallAmount);
    const drillMountAmount = parseFloat(this.pricingForm.value.drillMountAmount);
    const materials: MaterialOption[] = (this.materials.getRawValue() || []).map(
      (item: { label: string; price: number | string }) => ({
        label: String(item.label || '').trim(),
        price: Math.round(parseFloat(String(item.price)) * 100) / 100,
      })
    );

    if (
      [dutyAmount, nosepadsInstallAmount, drillMountAmount].some(
        (amount) => isNaN(amount) || amount < 0
      ) ||
      materials.some((item) => !item.label || isNaN(item.price) || item.price < 0)
    ) {
      return;
    }

    this.saving = true;
    this.settingsService
      .updatePricingSettings({
        dutyAmount,
        nosepadsInstallAmount,
        drillMountAmount,
        materials,
      })
      .subscribe({
        next: (settings) => {
          this.saving = false;
          this.applyPricingToForm(settings);
          this.notificationService.show('success', 'PRICING SETTINGS SAVED');
        },
        error: () => {
          this.saving = false;
          this.notificationService.show('error', 'FAILED TO SAVE PRICING SETTINGS');
        },
      });
  }

  private applyPricingToForm(settings: {
    dutyAmount: number;
    nosepadsInstallAmount: number;
    drillMountAmount: number;
    materials?: MaterialOption[];
  }): void {
    this.pricingForm.patchValue({
      dutyAmount: settings.dutyAmount,
      nosepadsInstallAmount: settings.nosepadsInstallAmount,
      drillMountAmount: settings.drillMountAmount,
    });
    this.setMaterials(settings.materials || []);
  }
}
